import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { OnboardingProvider, useOnboarding } from './context/OnboardingContext';
import { SearchExecutionProvider } from './context/SearchExecutionContext';
import { RTLProvider } from './context/RTLContext';
import { useAuth } from './context/AuthContext';
import DashboardLayout from './components/Layout/DashboardLayout';
import AISearchRankingAnalyzerRefactored from './components/AISearchRankingAnalyzerRefactored';
import ResultsDashboard from './pages/ResultsDashboard';
import AccountOverview from './pages/AccountOverview';
import ExecutionHistory from './pages/ExecutionHistory';
import AccountSettings from './pages/AccountSettings';
import NotFound from './pages/NotFound';
import AdminDashboard from './pages/admin/AdminDashboard';
import AuthGuard from './components/Auth/AuthGuard';
import SimpleOnboardingModal from './components/Onboarding/SimpleOnboardingModal';
import PendingReviewScreen from './components/Onboarding/PendingReviewScreen';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import './simplified.css'; // Simplified UI overrides
import './styles/matdash-theme.css'; // MatDash theme variables and global styles
import './styles/modern-app.css'; // Modern design system
import './styles/modern-auth.css'; // Modern auth styles
import './styles/dark-mode.css'; // Dark mode styles
import './styles/final-overrides.css'; // Final overrides - MUST load last

// Protected Route wrapper - ensures authentication and onboarding completion
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();

  // Not authenticated - redirect to home
  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  // Authenticated but onboarding not complete - show children but onboarding modal will overlay
  // This allows the route to load but the modal prevents interaction
  return children;
};

function AppContent() {
  const { isAuthenticated, logout } = useAuth();
  const { showOnboarding, showPendingReview, closeOnboarding } = useOnboarding();
  
  // Global keyboard shortcut for logout (Ctrl/Cmd + Shift + L)
  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'L') {
        e.preventDefault();
        console.log('Force logout via keyboard shortcut');
        logout();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [logout]);
  
  return (
    <AuthGuard>
        <div className="App min-h-screen relative">
          <div className="app-background"></div>
          <div className="relative z-10">
            <main>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={
                  isAuthenticated ? <Navigate to="/dashboard" replace /> : <AISearchRankingAnalyzerRefactored />
                } />
                
                {/* Protected routes with dashboard layout */}
                <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                  <Route path="/dashboard" element={<AccountOverview />} />
                  <Route path="/dashboard/history" element={<ExecutionHistory />} />
                  <Route path="/dashboard/settings" element={<AccountSettings />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/search" element={<AISearchRankingAnalyzerRefactored />} />
                  <Route path="/results/:batchId" element={<ResultsDashboard />} />
                </Route>
                
                {/* Catch all - redirect to home */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </div>
          
          {/* Simple Onboarding Modal */}
          <SimpleOnboardingModal 
            isOpen={showOnboarding} 
            onClose={closeOnboarding} 
          />
          
          {/* Pending Review Screen */}
          {showPendingReview && <PendingReviewScreen />}
        </div>
      </AuthGuard>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <RTLProvider>
          <AuthProvider>
            <Router>
              <OnboardingProvider>
                <SearchExecutionProvider>
                  <AppContent />
                </SearchExecutionProvider>
              </OnboardingProvider>
            </Router>
          </AuthProvider>
        </RTLProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;